import React from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Chip,
  Divider,
  Alert,
  CircularProgress,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import PersonIcon from '@mui/icons-material/Person';
import BusinessIcon from '@mui/icons-material/Business';
import LanguageIcon from '@mui/icons-material/Language';
import KeyIcon from '@mui/icons-material/Key';

/**
 * Component to display metadata lookup results and allow user confirmation.
 */
function DocxMetadataPreview({ response, onConfirm, onReject, isUploading }) {
  const { filename, extracted_id, digital_asset_key, metadata, match_found } = response;

  const getAuthorsDisplay = () => {
    if (!metadata?.copyright_holders) return null;
    const { authors, editors } = metadata.copyright_holders;
    const people = [...(authors || []), ...(editors || [])];
    if (people.length === 0) return null;

    const first = people[0];
    const name = `${first.first_name || ''} ${first.last_name || ''}`.trim();
    if (people.length === 1) return name;
    return `${name} et al.`;
  };

  if (!match_found) {
    return (
      <Paper sx={{ p: 3, mb: 3 }}>
        <Alert severity="warning" sx={{ mb: 2 }}>
          <Typography variant="subtitle1" fontWeight="bold">
            No metadata found
          </Typography>
          <Typography variant="body2">
            Could not find a product with digital_asset_collection_key: <strong>{digital_asset_key}</strong>
          </Typography>
          <Typography variant="body2" sx={{ mt: 1 }}>
            Extracted ID from filename "{filename}": <strong>{extracted_id}</strong>
          </Typography>
        </Alert>
        <Button variant="outlined" color="error" onClick={onReject} startIcon={<CancelIcon />}>
          Select Different File
        </Button>
      </Paper>
    );
  }

  return (
    <Paper sx={{ p: 3, mb: 3 }}>
      <Typography variant="h6" gutterBottom>
        Metadata Match Found
      </Typography>
      <Divider sx={{ mb: 2 }} />

      {/* Match info */}
      <Box sx={{ mb: 3, p: 2, bgcolor: 'success.light', borderRadius: 1, opacity: 0.9 }}>
        <Typography variant="body2" color="success.contrastText">
          File: <strong>{filename}</strong> → Matched key: <strong>{digital_asset_key}</strong>
        </Typography>
      </Box>

      {/* Metadata display */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
          {metadata.title || 'Untitled'}
        </Typography>
        {metadata.subtitle && (
          <Typography variant="body2" color="text.secondary" gutterBottom>
            {metadata.subtitle}
          </Typography>
        )}

        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 2 }}>
          {metadata.pubType && (
            <Chip
              icon={<MenuBookIcon sx={{ fontSize: '0.9rem' }} />}
              label={metadata.pubType}
              size="small"
              color="primary"
              sx={{ height: 24 }}
            />
          )}
          {metadata.ean && (
            <Chip
              icon={<KeyIcon sx={{ fontSize: '0.9rem' }} />}
              label={`EAN: ${metadata.ean}`}
              size="small"
              variant="outlined"
              sx={{ height: 24 }}
            />
          )}
          {metadata.isbn && (
            <Chip
              label={`ISBN: ${metadata.isbn}`}
              size="small"
              variant="outlined"
              sx={{ height: 24 }}
            />
          )}
          {getAuthorsDisplay() && (
            <Chip
              icon={<PersonIcon sx={{ fontSize: '0.9rem' }} />}
              label={getAuthorsDisplay()}
              size="small"
              variant="outlined"
              sx={{ height: 24 }}
            />
          )}
          {metadata.publishing_house_term && (
            <Chip
              icon={<BusinessIcon sx={{ fontSize: '0.9rem' }} />}
              label={metadata.publishing_house_term}
              size="small"
              variant="outlined"
              sx={{ height: 24 }}
            />
          )}
          {metadata.language_term && (
            <Chip
              icon={<LanguageIcon sx={{ fontSize: '0.9rem' }} />}
              label={metadata.language_term}
              size="small"
              variant="outlined"
              sx={{ height: 24 }}
            />
          )}
          {metadata.copyright_year && (
            <Chip
              label={`© ${metadata.copyright_year}`}
              size="small"
              variant="outlined"
              sx={{ height: 24 }}
            />
          )}
        </Box>
      </Box>

      <Divider sx={{ mb: 2 }} />

      {/* Action buttons */}
      <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
        <Button
          variant="outlined"
          color="error"
          onClick={onReject}
          startIcon={<CancelIcon />}
          disabled={isUploading}
        >
          Reject
        </Button>
        <Button
          variant="contained"
          color="success"
          onClick={onConfirm}
          startIcon={isUploading ? <CircularProgress size={20} color="inherit" /> : <CheckCircleIcon />}
          disabled={isUploading}
        >
          {isUploading ? 'Uploading...' : 'Confirm & Upload'}
        </Button>
      </Box>
    </Paper>
  );
}

export default DocxMetadataPreview;
